import type { BestRecords } from '../logic/bestScoreStore';
import { formatMeters } from '../logic/measurement';
import { formatScore } from '../logic/score';
import type { HitboxMode } from '../logic/hitboxDebug';
import type { LayoutMode } from '../logic/viewportMetrics';

const PLACEMENT_POPUP_DURATION_MS = 1_200;

export interface HudState {
  currentPoint: number;
  currentMeters: number;
  bestPoint: number;
  bestMeters: number;
  animalName: string;
}

export interface SpawnLineLayout {
  left: number;
  top: number;
  width: number;
}

export interface GameplayControls {
  rotateLeft(): void;
  releaseRotateLeft(): void;
  rotateRight(): void;
  releaseRotateRight(): void;
  drop(): void;
}

function bindPressAndHold(
  button: HTMLButtonElement,
  onPress: () => void,
  onRelease: () => void,
): void {
  let activePointerId: number | null = null;
  button.addEventListener('pointerdown', (event) => {
    if (button.disabled || activePointerId !== null) return;
    activePointerId = event.pointerId;
    button.setPointerCapture(event.pointerId);
    onPress();
  });
  const release = (event: PointerEvent) => {
    if (event.pointerId !== activePointerId) return;
    activePointerId = null;
    onRelease();
  };
  button.addEventListener('pointerup', release);
  button.addEventListener('pointercancel', release);
  button.addEventListener('lostpointercapture', release);
  button.addEventListener('click', (event) => {
    if (event.detail !== 0) return;
    onPress();
    onRelease();
  });
  button.addEventListener('contextmenu', (event) => event.preventDefault());
}

export class GameUi {
  private readonly stageRoot: HTMLElement;
  private readonly hudRoot: HTMLElement;
  private readonly controlsRoot: HTMLElement;
  private readonly layoutRoot: HTMLElement;
  private placementPopupTimer: number | null = null;

  constructor(stageRoot: HTMLElement, hudRoot: HTMLElement, controlsRoot: HTMLElement) {
    this.stageRoot = stageRoot;
    this.hudRoot = hudRoot;
    this.controlsRoot = controlsRoot;
    const layoutRoot = stageRoot.closest<HTMLElement>('#app-shell');
    if (!layoutRoot) throw new Error('Missing #app-shell');
    this.layoutRoot = layoutRoot;
  }

  showTitle(records: BestRecords, onStart: () => void): void {
    this.setLayout('full');
    this.hudRoot.replaceChildren();
    this.controlsRoot.replaceChildren();
    this.stageRoot.innerHTML = `
      <section class="surface title-surface" data-testid="title-screen">
        <div class="title-lockup" aria-label="逆どうぶつタワー">
          <span class="title-inverse">逆</span><span>どうぶつ</span><span>タワー</span>
        </div>
        <div class="mini-ground" aria-hidden="true"><span></span></div>
        <div class="title-records" aria-label="ベスト記録">
          <p class="best-title"><span>BEST m</span><strong data-testid="title-best-meters">${formatMeters(records.bestMeters)}</strong></p>
          <p class="best-title"><span>BEST Point</span><strong data-testid="title-best-point">${formatScore(records.bestPoint)}</strong></p>
        </div>
        <button class="primary-button" data-testid="start-button" type="button">はじめる</button>
        <p class="controls-copy desktop-controls-copy">移動: Pointer / A D　回転: Q E　DROP: Click / Space</p>
        <p class="controls-copy mobile-controls-copy">ドラッグ: 移動　↺ ↻: 回転　DROP: 落とす</p>
      </section>`;
    this.stageRoot.querySelector<HTMLButtonElement>('[data-testid="start-button"]')!.addEventListener('click', onStart, { once: true });
  }

  showGame(controls: GameplayControls): void {
    this.setLayout('game');
    this.stageRoot.innerHTML = `
      <section class="surface game-surface" data-testid="game-screen">
        <div class="spawn-line" data-testid="spawn-line" aria-hidden="true"></div>
        <div class="placement-popup" data-testid="placement-popup" aria-live="polite"></div>
      </section>`;
    this.hudRoot.innerHTML = `
      <section class="hud-rail" data-testid="game-hud">
        <div class="hud-current">
          <span class="hud-label">いまの高さ</span>
          <strong class="meter-value" data-testid="current-meters">0.0 m</strong>
          <div class="point-value"><span>Point</span><strong data-testid="score">0点</strong></div>
        </div>
        <div class="hud-records">
          <div><span>BEST m</span><strong data-testid="best-meters">0.0 m</strong></div>
          <div><span>BEST Point</span><strong data-testid="best">0点</strong></div>
        </div>
        <div class="hud-next"><span>どうぶつ</span><strong data-testid="next-animal">—</strong></div>
        <div class="hud-controls" data-testid="controls">
          <p><kbd>A</kbd> <kbd>D</kbd> / Pointer <span>移動</span></p>
          <p><kbd>Q</kbd> <kbd>E</kbd> <span>回転</span></p>
          <p>Click / <kbd>Space</kbd> <span>DROP</span></p>
        </div>
      </section>`;
    this.controlsRoot.innerHTML = `
      <div class="mobile-control-dock" data-testid="mobile-controls">
        <button type="button" data-testid="rotate-left" aria-label="左へ15度回転">↺</button>
        <button class="mobile-drop-button" type="button" data-testid="mobile-drop">DROP</button>
        <button type="button" data-testid="rotate-right" aria-label="右へ15度回転">↻</button>
      </div>`;
    bindPressAndHold(
      this.controlsRoot.querySelector<HTMLButtonElement>('[data-testid="rotate-left"]')!,
      controls.rotateLeft,
      controls.releaseRotateLeft,
    );
    bindPressAndHold(
      this.controlsRoot.querySelector<HTMLButtonElement>('[data-testid="rotate-right"]')!,
      controls.rotateRight,
      controls.releaseRotateRight,
    );
    this.controlsRoot.querySelector<HTMLButtonElement>('[data-testid="mobile-drop"]')!.addEventListener('click', controls.drop);
  }

  setGameplayControlsEnabled(enabled: boolean): void {
    for (const button of this.controlsRoot.querySelectorAll<HTMLButtonElement>('button')) button.disabled = !enabled;
  }

  setHitboxDebugMode(mode: HitboxMode | null): void {
    this.stageRoot.querySelector('[data-testid="hitbox-mode"]')?.remove();
    if (mode === null) return;
    const surface = this.stageRoot.querySelector<HTMLElement>('[data-testid="game-screen"]');
    if (!surface) return;
    const label = document.createElement('div');
    label.className = 'hitbox-mode-label';
    label.dataset.testid = 'hitbox-mode';
    label.textContent = `HITBOX: ${mode}`;
    surface.append(label);
  }

  setViewportLayout(layoutMode: LayoutMode): void {
    this.layoutRoot.dataset.viewportLayout = layoutMode;
  }

  updateSpawnLineLayout(layout: SpawnLineLayout): void {
    const line = this.stageRoot.querySelector<HTMLElement>('[data-testid="spawn-line"]');
    if (!line) return;
    line.style.left = `${layout.left}px`;
    line.style.top = `${layout.top}px`;
    line.style.width = `${layout.width}px`;
  }

  updateHud(state: HudState): void {
    this.setText('[data-testid="current-meters"]', formatMeters(state.currentMeters));
    this.setText('[data-testid="score"]', formatScore(state.currentPoint));
    this.setText('[data-testid="best-meters"]', formatMeters(state.bestMeters));
    this.setText('[data-testid="best"]', formatScore(state.bestPoint));
    this.setText('[data-testid="next-animal"]', state.animalName || '判定中…');
  }

  showPlacement(points: number): void {
    const popup = this.stageRoot.querySelector<HTMLElement>('[data-testid="placement-popup"]');
    if (!popup) return;
    if (this.placementPopupTimer !== null) window.clearTimeout(this.placementPopupTimer);
    popup.textContent = formatScore(points);
    popup.classList.remove('is-visible');
    requestAnimationFrame(() => popup.classList.add('is-visible'));
    this.placementPopupTimer = window.setTimeout(() => {
      popup.classList.remove('is-visible');
      this.placementPopupTimer = null;
    }, PLACEMENT_POPUP_DURATION_MS);
  }

  showGameOver(currentPoint: number, currentMeters: number, records: BestRecords, reason: string, onRetry: () => void): void {
    this.setLayout('full');
    this.hudRoot.replaceChildren();
    this.controlsRoot.replaceChildren();
    this.stageRoot.innerHTML = `
      <section class="surface game-over-surface" data-testid="game-over-screen">
        <div class="game-over-card">
          <p class="eyebrow">OUT!</p>
          <h1>GAME OVER</h1>
          <p class="game-over-reason">${reason}</p>
          <div class="final-scores">
            <div><span>今回 m</span><strong data-testid="final-meters">${formatMeters(currentMeters)}</strong></div>
            <div><span>今回 Point</span><strong data-testid="final-score">${formatScore(currentPoint)}</strong></div>
            <div><span>BEST m</span><strong data-testid="final-best-meters">${formatMeters(records.bestMeters)}</strong></div>
            <div><span>BEST Point</span><strong data-testid="final-best-point">${formatScore(records.bestPoint)}</strong></div>
          </div>
          <button class="primary-button" data-testid="retry-button" type="button">もう一度</button>
        </div>
      </section>`;
    this.stageRoot.querySelector<HTMLButtonElement>('[data-testid="retry-button"]')!.addEventListener('click', onRetry, { once: true });
  }

  showRuntimeFailure(reason: string, onRetry: () => void): void {
    this.setLayout('full');
    this.hudRoot.replaceChildren();
    this.controlsRoot.replaceChildren();
    this.stageRoot.innerHTML = `
      <section class="surface game-over-surface" data-testid="runtime-failure-screen">
        <div class="game-over-card">
          <p class="eyebrow">RUNTIME ERROR</p>
          <h1>物理演算を停止しました</h1>
          <p class="game-over-reason">${reason}</p>
          <p>これはOUTによるGAME OVERではありません。</p>
          <button class="primary-button" data-testid="runtime-retry-button" type="button">再起動</button>
        </div>
      </section>`;
    this.stageRoot.querySelector<HTMLButtonElement>('[data-testid="runtime-retry-button"]')!.addEventListener('click', onRetry, { once: true });
  }

  clear(): void {
    this.setLayout('full');
    this.stageRoot.replaceChildren();
    this.hudRoot.replaceChildren();
    this.controlsRoot.replaceChildren();
  }

  private setText(selector: string, value: string): void {
    const element = this.hudRoot.querySelector<HTMLElement>(selector);
    if (element) element.textContent = value;
  }

  private setLayout(layout: 'full' | 'game'): void {
    this.layoutRoot.dataset.layout = layout;
  }
}
